import { lens } from "@dhmk/zustand-lens";
import z from "zod";
import { imagineStore } from "../imagine.store";
import { workspaceStateSchema } from "@imagine/realtime/schemas";

type WorkspaceStateState = {
  steps: z.infer<typeof workspaceStateSchema>["steps"];
  isInProgress: boolean;
  isMakingChanges: boolean;
  anyChangesMade: boolean; // Changes made in this generation
  hidePreview: boolean;
  error: string | null;
};

type WorkspaceStateActions = {
  update: (data: z.infer<typeof workspaceStateSchema>) => void;
  reset: () => void;
  setError: (error: string | null) => void;
};

const defaultWorkspaceState: WorkspaceStateState = {
  steps: [],
  isInProgress: false,
  isMakingChanges: false,
  anyChangesMade: false,
  hidePreview: false,
  error: null,
};

export type WorkspaceStateSlice = WorkspaceStateState & WorkspaceStateActions;

export const workspaceStateSlice = lens<WorkspaceStateSlice>(
  (set, get, api) => ({
    ...defaultWorkspaceState,
    setError: (error) => {
      set({ error });
    },
    update: (data) => {
      const { steps } = data;
      const isInProgress = steps?.some((s) => s.status === "in-progress");
      const isMakingChanges = steps?.some(
        (s) => s.id === "making-changes" && s.status === "in-progress",
      );

      if (isMakingChanges && !get().anyChangesMade) {
        set({ anyChangesMade: true });
      }

      if (data.previewUrl) {
        imagineStore
          .getState()
          .previewState.setPreviewUrl(new URL(data.previewUrl));
      }

      set({
        steps: steps,
        isInProgress,
        isMakingChanges,
        error: data.error ?? null,
      });
    },
    reset: () => {
      set(defaultWorkspaceState);
    },
  }),
);
